import { roundMoney } from './money';

// The guest's language decides the separators. es uses de-DE: the same "815.000" and
// "1.234,50", but CLDR minimumGroupingDigits 1, so four-digit amounts group on every
// engine (es inherits 2 and prints "1500" wherever `useGrouping: true` keeps its
// pre-Intl-v3 meaning, Safari < 15.4).
const LOCALE_FOR_LANGUAGE: Record<string, string> = {
  es: 'de-DE',
  en: 'en-US',
  pt: 'pt-BR',
  fr: 'fr-FR',
  de: 'de-DE',
};

// Shown in whole units, truncated.
const WHOLE_UNIT_CURRENCIES = new Set(['CLP', 'PYG']);

/** The code formatMoney prints: trimmed and upper-cased; null when blank or not a string. */
export function currencyCode(currency: string | null | undefined): string | null {
  const trimmed = typeof currency === 'string' ? currency.trim() : '';
  return trimmed === '' ? null : trimmed.toUpperCase();
}

/** The amount as the guest reads it, without the currency code. */
export function formatMoneyAmount(amount: number, currency: string | null | undefined, uiLanguage: string): string {
  const code = currencyCode(currency);
  const value = code !== null && WHOLE_UNIT_CURRENCIES.has(code) ? Math.trunc(amount) : roundMoney(amount);
  const decimals = Number.isInteger(value) ? 0 : 2;
  // An i18n instance that has not resolved a language yet reads as English (useLanguage does the same).
  const locale = LOCALE_FOR_LANGUAGE[(uiLanguage || 'en').split('-')[0]] ?? 'en-US';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(value);
}

/** "ARS 815.000": the ISO code (never a symbol) and the amount in the guest's language. */
export function formatMoney(amount: number, currency: string | null | undefined, uiLanguage: string): string {
  const code = currencyCode(currency);
  const formatted = formatMoneyAmount(amount, code, uiLanguage);
  return code ? `${code} ${formatted}` : formatted;
}
